import React from 'react'
import { useTranslation } from 'react-i18next'
import { ShiftReport } from '@/types'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import SectionTitle from './SectionTitle'

interface ShiftReportsTableProps {
  shifts: ShiftReport[]
  formatNumber: (num: number | string | undefined | null) => string
  formatDate: (dateString: string) => string
  isLoading?: boolean
  getRecentShifts: () => ShiftReport[]
}

// LTR Number component (matching Flutter's Directionality)
const LTRNum: React.FC<{
  text: string
  style?: React.CSSProperties
}> = ({ text, style }) => {
  return (
    <span style={{ direction: 'ltr', display: 'inline-block', ...style }}>
      {text}
    </span>
  )
}

const ShiftReportsTable: React.FC<ShiftReportsTableProps> = ({
  shifts,
  formatNumber,
  formatDate,
  isLoading = false,
  getRecentShifts,
}) => {
  const { t } = useTranslation()

  const recentShifts = getRecentShifts()

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
        <SectionTitle titleKey="shift_reports" />
        <div className="py-2.5 flex justify-center">
          <div className="w-5.5 h-5.5 border-2 border-gray-300 border-t-brand-base rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  if (!recentShifts || recentShifts.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
        <SectionTitle titleKey="shift_reports" />
        <div className="h-10 flex items-center justify-center text-gray-500 font-['IBM_Plex_Sans_Arabic']">
          {t('no_shifts')}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl p-4 flex flex-col gap-2.5">
      <SectionTitle titleKey="shift_reports" />
      <div className="h-2.5" /> {/* SizedBox(height: 10) */}

      {/* Header Bar */}
      <div
        className="h-9 px-2.5 rounded-[10px] flex items-center"
        style={{ backgroundColor: '#F5F5F5' }}
      >
        <div className="flex-1 text-start text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('date')}
        </div>
        <div className="flex-1 text-start text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('opening_balance')}
        </div>
        <div className="flex-1 text-start text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('total_sales')}
        </div>
        <div className="flex-1 text-end text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('balance')}
        </div>
        <div className="flex-1 text-end text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('closing_balance')}
        </div>
      </div>

      <div className="h-1.5" /> {/* SizedBox(height: 6) */}

      {/* Shifts List */}
      <div className="flex flex-col gap-1.5">
        {recentShifts.map((shift, index) => {
          const isActive = shift.status === 'active'

          return (
            <div
              key={shift.id || index}
              className="px-2.5 py-2 rounded-lg"
              style={{
                backgroundColor: isActive
                  ? '#f0fdf4' // green.shade50
                  : index % 2 === 0
                  ? '#F5F5F5' // grey.shade100
                  : 'white',
                border: isActive
                  ? '1px solid #bbf7d0' // green.shade200
                  : 'none',
              }}
            >
              <div className="flex items-center">
                {/* Date (flex: 1, start) */}
                <div className="flex-1 text-start">
                  <span className="font-medium font-['IBM_Plex_Sans_Arabic'] text-xs">
                    {shift.startedAt}
                  </span>
                </div>

                {/* Opening Balance (flex: 1, start) */}
                <div className="flex-1 flex items-baseline gap-1 justify-start">
                  <LTRNum
                    text={formatNumber(
                      typeof shift.openingAmount === 'string'
                        ? parseFloat(shift.openingAmount) || 0
                        : shift.openingAmount || 0
                    )}
                    style={{
                      fontWeight: 500,
                      fontFamily: 'IBM_Plex_Sans_Arabic',
                      fontSize: '12px',
                      color: '#000000',
                    }}
                  />
                  <CurrencyDisplay className="w-3 h-3" />
                </div>

                {/* Total Sales / Balance (flex: 1, start) */}
                <div className="flex-1 flex items-baseline gap-1 justify-start">
                  <LTRNum
                    text={formatNumber(
                      typeof shift.totalSales === 'string'
                        ? parseFloat(shift.totalSales) || 0
                        : shift.totalSales || 0
                    )}
                    style={{
                      fontWeight: 600,
                      fontFamily: 'IBM_Plex_Sans_Arabic',
                      fontSize: '12px',
                      color: '#000000',
                    }}
                  />
                  <CurrencyDisplay className="w-3 h-3" />
                </div>

                {/* Closing Balance (flex: 1, end) */}
                <div className="flex-1 flex items-baseline gap-1 justify-end">
                  <LTRNum
                    text={
                      shift.closingAmount
                        ? formatNumber(
                            typeof shift.closingAmount === 'string'
                              ? parseFloat(shift.closingAmount) || 0
                              : shift.closingAmount || 0
                          )
                        : '-'
                    }
                    style={{
                      fontWeight: 600,
                      fontFamily: 'IBM_Plex_Sans_Arabic',
                      fontSize: '12px',
                      color: '#000000',
                    }}
                  />
                  {shift.closingAmount && (
                    <CurrencyDisplay className="w-3 h-3" />
                  )}
                </div>

                {/* Entered Closing Balance (flex: 1, end) */}
                <div className="flex-1 flex items-baseline gap-1 justify-end">
                  <LTRNum
                    text={
                      shift.enteredClosingBalance
                        ? formatNumber(
                            typeof shift.enteredClosingBalance === 'string'
                              ? parseFloat(shift.enteredClosingBalance) || 0
                              : shift.enteredClosingBalance || 0
                          )
                        : '-'
                    }
                    style={{
                      fontWeight: 500,
                      fontFamily: 'IBM_Plex_Sans_Arabic',
                      fontSize: '12px',
                      color: '#000000',
                    }}
                  />
                  {shift.enteredClosingBalance && (
                    <CurrencyDisplay className="w-3 h-3" />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ShiftReportsTable
